import { ref, watch } from 'vue';
import type { Chat, ChatMessage } from '@/types/chat';
import { ServiceLocator } from '@/services/ServiceLocator';
import type { MessageFeedbackRating } from '@/infrastructure/api/client_typescript';

// --- Reactive State ---
export const chats = ref<Chat[]>([]);
export const currentChat = ref<Chat | null>(null);
export const responseChatIds = ref(new Set<string>());
const chatStorage = ServiceLocator.getChatStorage();

export const autoScrollTrigger = ref(0);
export const loadingSharedChat = ref(false);

const handledFailedMessageIds = new Set<string>();
const knownPendingChatIds = new Set<string>();
const trackingMessages = new Set<string>();

// --- Persist chats on change ---
watch(
  chats,
  (newChats) => {
    chatStorage.setChats(newChats);
  },
  { deep: true }
);

// --- Persist currentChat ID on change ---
watch(currentChat, (chat: Chat | null) => {
  if (chat) {
    chatStorage.setCurrentChatId(String(chat.id));
  }
});

export function isChatResponding(chatId: string | null | undefined): boolean {
  if (!chatId) return false;
  return responseChatIds.value.has(chatId);
}

function markChatAsResponding(chatId: string) {
  responseChatIds.value.add(chatId);
}

function unmarkChatAsResponding(chatId: string) {
  responseChatIds.value.delete(chatId);
  knownPendingChatIds.delete(chatId);
}

// --- Load mock data if needed ---
export function loadMockChats(mockData: Chat[]) {
  chats.value = mockData;
}

// --- Clear all chats (used when leaving shared mode) ---
export function clearChats() {
  chats.value = [];
  currentChat.value = null;
}

// --- Create a new chat ---
export function startNewChat() {
  currentChat.value = {
    id: 'new',
    messages: [],
    last_message_at: new Date(),
    title: 'New Chat',
  };
}

export async function refreshChats() {
  const chatService = ServiceLocator.getChatService();
  const fetchedChats = await chatService.listChats();
  loadMockChats(fetchedChats);

  // Kick off pending tracking **asynchronously**, without blocking UI
  trackAllPendingMessagesInBackground();
}

export async function refreshChatMessages(chatId: string) {
  const id = String(chatId);
  const chatService = ServiceLocator.getChatService();
  const { messages, userSettings } = await chatService.listChatMessages(chatId);

  const chat = chats.value.find((c) => c.id === chatId);

  if (chat && userSettings) {
    chat.userSettings = userSettings;
  }

  const mergedMessages = messages.map((serverMsg) => {
    const contextDocuments = serverMsg.context_documents || [];
    const contextFolders = serverMsg.context_folders || [];

    if (serverMsg.sender === 'user') {
      return {
        ...serverMsg,
        selectedDocuments: contextDocuments.map((doc) => ({
          id: doc.id,
          title: doc.title,
          folder: doc.folder || '',
        })),
        context_document_ids: contextDocuments.map((doc) => doc.id),

        selectedFolders: contextFolders.map((folder) => ({
          id: folder.id,
          title: folder.title,
        })),
        context_folder_ids: contextFolders.map((folder) => folder.id),
      };
    }

    return {
      ...serverMsg,
    };
  });

  // const chat = chats.value.find((chat) => String(chat.id) === id);
  if (chat) {
    chat.messages = mergedMessages;
  }

  // ✅ keep currentChat in sync (critical for the UI to exit skeleton)
  if (currentChat.value && String(currentChat.value.id) === id) {
    currentChat.value.messages = mergedMessages;
    if (userSettings) currentChat.value.userSettings = userSettings;
  }

  // ✅ Track pending for this chat
  const hasPending = mergedMessages.some((msg) => msg.status === 'pending');
  if (hasPending) knownPendingChatIds.add(chatId);

  // ✅ Resume tracking for pending messages
  mergedMessages
    .filter((msg) => msg.status === 'pending')
    .forEach((msg) => {
      markChatAsResponding(chatId);
      trackMessageUpdates(chatId, msg.id);
    });
}

async function trackAllPendingMessagesInBackground() {
  const chatService = ServiceLocator.getChatService();
  const chatIdsToCheck = new Set<string>([...knownPendingChatIds]);

  if (currentChat.value?.id && currentChat.value.id !== 'new') {
    chatIdsToCheck.add(String(currentChat.value.id));
  }

  for (const chatId of chatIdsToCheck) {
    try {
      const id = String(chatId);
      const { messages, userSettings } = await chatService.listChatMessages(id);
      const hasPending = messages.some((msg) => msg.status === 'pending');

      const chatInStore = chats.value.find((c) => c.id === id);
      if (chatInStore) {
        chatInStore.messages = messages;
        if (userSettings) chatInStore.userSettings = userSettings;
      }

      // ✅ ALSO update the visible chat
      if (currentChat.value && String(currentChat.value.id) === id) {
        currentChat.value.messages = messages;
        if (userSettings) currentChat.value.userSettings = userSettings;
      }

      if (hasPending) {
        knownPendingChatIds.add(id);
        messages
          .filter((msg) => msg.status === 'pending')
          .forEach((msg) => {
            markChatAsResponding(id);
            trackMessageUpdates(id, msg.id);
          });
      } else {
        unmarkChatAsResponding(id);
      }
    } catch {
      return;
    }
  }
}

export function handleFailedMessage(
  chatId: string,
  text = '',
  id?: string,
  followLinks = false,
  model = ''
) {
  if (!id) return; // We need ID to locate the pending bot message
  if (handledFailedMessageIds.has(id)) return;
  handledFailedMessageIds.add(id);

  const chat = chats.value.find((c) => c.id === chatId);
  if (!chat) return;

  const index = chat.messages.findIndex(
    (msg) => msg.id === id && msg.sender === 'bot'
  );
  if (index === -1) return;

  const failedMessage: ChatMessage = {
    id,
    sender: 'bot',
    text,
    status: 'failed',
    relatedDocuments: null,
    related_client_history_passages: [],
    related_case_law_passages: [],
    context_document_ids: [],
    selectedDocuments: [],
    selectedFolders: [],
    context_folder_ids: [],
    follow_links: followLinks,
    model: model,
  };

  // Replace pending message with failed message
  chat.messages.splice(index, 1, failedMessage);

  if (currentChat.value?.id === chatId) {
    const currentIndex = currentChat.value.messages.findIndex(
      (m) => m.id === id
    );
    if (currentIndex !== -1) {
      currentChat.value.messages.splice(currentIndex, 1, failedMessage);
    }
  }

  autoScrollTrigger.value++;
  unmarkChatAsResponding(chatId);
}

// --- Send a message ---

export function sendMessage(
  text: string,
  context_document_ids: string[],
  context_folder_ids: string[],
  followLinks: boolean,
  model: string
) {
  if (text == 'sentry') {
    throw new Error('test sentry');
  }

  const chatService = ServiceLocator.getChatService();
  const chatId = currentChat.value?.id;
  const isNewChat = chatId === undefined || chatId === 'new';

  if (!isNewChat && isChatResponding(chatId)) {
    return;
  }

  if (!isNewChat && chatId) {
    // ✅ optimistic sticky settings on existing chat
    const chat = chats.value.find((c) => c.id === chatId);
    if (chat) {
      chat.userSettings = {
        ...(chat.userSettings ?? {}),
        bot_model: model,
        follow_links: followLinks,
        // Note: not writing context_* here because UI already carries it
      };
    }

    markChatAsResponding(chatId);
    chatService
      .sendMessageInExistingChat(
        chatId,
        text,
        context_document_ids,
        context_folder_ids,
        followLinks,
        model
      )
      .then(async (response) => {
        if (currentChat.value) {
          await refreshChatMessages(chatId);
          selectChat(chatId);
          autoScrollTrigger.value++; // ✅ scroll after message rendered
        }

        if (response.bot.status === 'pending') {
          trackMessageUpdates(chatId, response.bot.id);
        } else {
          unmarkChatAsResponding(chatId);
        }
        return chatId;
      });
  } else {
    chatService
      .sendMessageInNewChat(
        text,
        context_document_ids,
        context_folder_ids,
        followLinks,
        model
      )
      .then(async (response) => {
        const chatId = response.user.chat_id;

        // ✅ optimistic sticky on the new chat (if it's already in the store after refreshChats)
        const seeded = chats.value.find((c) => c.id === chatId);
        if (seeded) {
          seeded.userSettings = {
            bot_model: model,
            follow_links: followLinks,
          };
        }

        markChatAsResponding(chatId);

        await refreshChats();
        await refreshChatMessages(chatId);
        selectChat(chatId);
        autoScrollTrigger.value++; // ✅ scroll after loading messages into new chat

        if (response.bot.status === 'pending') {
          trackMessageUpdates(chatId, response.bot.id);
        } else {
          unmarkChatAsResponding(chatId);
        }
        return chatId;
      });
  }
}

// --- Select an existing chat ---
export async function selectChat(chatId: string) {
  const id = String(chatId);
  const selected = chats.value.find((chat) => String(chat.id) === id);

  if (selected) {
    currentChat.value = selected;

    if (selected.messages.length === 0) {
      await refreshChatMessages(id); // 👈 wait for messages
      autoScrollTrigger.value++;
    } else {
      autoScrollTrigger.value++;
    }
  } else {
    // ✅ Chat already has messages, but we still need to scroll after re-render
    autoScrollTrigger.value++;
    return;
  }
}

export function trackMessageUpdates(chatId: string, messageId: string) {
  const key = `${chatId}:${messageId}`;

  // ✅ Avoid starting the same polling twice
  if (trackingMessages.has(key)) return;
  trackingMessages.add(key);

  let retries = 600;

  async function doJob() {
    const chatService = ServiceLocator.getChatService();
    const message = await chatService.getChatMessage(chatId, messageId);

    if (message.status === 'pending') {
      retries--;
      if (retries < 0) {
        clearInterval(jobInterval);
        unmarkChatAsResponding(chatId);
        trackingMessages.delete(key);
      }
      return;
    }
    clearInterval(jobInterval);
    trackingMessages.delete(key);

    if (message.status === 'failed') {
      handleFailedMessage(chatId, '', messageId);
      return;
    }

    if (message.status === 'ready') {
      const chat = chats.value.find((chat) => chat.id === chatId);

      if (!chat) {
        unmarkChatAsResponding(chatId);
        return;
      }

      const chatMessage = chat.messages.find(
        (message) => message.id === messageId
      );

      if (!chatMessage) {
        unmarkChatAsResponding(chatId);
        return;
      }

      chatMessage.status = message.status;
      chatMessage.text = message.text;
      chatMessage.relatedDocuments = message.relatedDocuments;
      chatMessage.related_client_history_passages =
        message.related_client_history_passages;
      chatMessage.related_case_law_passages = message.related_case_law_passages;

      const contextDocuments = message.context_documents || [];
      const contextFolders = message.context_folders || [];

      chatMessage.context_document_ids = contextDocuments.map((doc) => doc.id);
      chatMessage.selectedDocuments = contextDocuments.map((doc) => ({
        id: doc.id,
        title: doc.title,
        folder: doc.folder || '',
      }));

      chatMessage.context_folder_ids = contextFolders.map(
        (folder) => folder.id
      );
      chatMessage.selectedFolders = contextFolders.map((folder) => ({
        id: folder.id,
        title: folder.title,
      }));

      chat.last_message_at = new Date();
      autoScrollTrigger.value++; // ✅ trigger update that the message changed
      unmarkChatAsResponding(chatId);
      return;
    }
    unmarkChatAsResponding(chatId);
  }

  const jobInterval = setInterval(() => {
    doJob().catch(() => {
      trackingMessages.delete(key);
      handleFailedMessage(chatId, '', messageId);
    });
  }, 2000);
}

export async function loadSharedChat(sharedId: string) {
  const chatService = ServiceLocator.getChatService();

  try {
    const messages = await chatService.getMessagesFromSharedLink(sharedId);

    const sharedChat: Chat = {
      id: `shared-${sharedId}`,
      title: 'Shared Chat',
      messages,
      last_message_at: new Date(),
    };

    currentChat.value = sharedChat;
    chats.value = [sharedChat]; // ✅ required so layout recognizes it

    // ✅ Trigger scroll to bottom if needed
    autoScrollTrigger.value++;
  } catch {
    startNewChat(); // fallback if loading fails
  }
}

export async function sendMessageFeedback(
  msg: ChatMessage,
  rating: MessageFeedbackRating
) {
  if (!msg?.id || !currentChat.value?.id) return;

  try {
    await ServiceLocator.getChatService().addMessageFeedback({
      chat_id: currentChat.value.id,
      message_id: msg.id,
      rating,
      comment: '',
    });
  } catch {}
}

export async function sendMessageFeedbackComment(
  msg: ChatMessage,
  comment: string
) {
  if (!msg?.id || !currentChat.value?.id) return;

  try {
    await ServiceLocator.getChatService().addMessageFeedback({
      chat_id: currentChat.value.id,
      message_id: msg.id,
      rating: msg.feedback || 'dislike', // fallback to 'dislike'
      comment,
    });

    msg.feedbackPendingComment = false;
    msg.feedbackComment = comment;
  } catch {}
}

export async function stopCurrentGeneration(targetMessageId?: string) {
  const chat = currentChat.value;
  if (!chat || chat.id === 'new') return;

  const target =
    (targetMessageId &&
      chat.messages.find(
        (m) => m.id === targetMessageId && m.sender === 'bot'
      )) ||
    [...chat.messages]
      .reverse()
      .find((m) => m.sender === 'bot' && m.status === 'pending');

  if (!target) return;

  const chatId = String(chat.id);
  const messageId = target.id;

  await ServiceLocator.getChatService().stopMessageGeneration(
    chatId,
    messageId
  );

  // Stop the spinner immediately for this chat
  unmarkChatAsResponding(chatId);

  // Pull fresh messages so UI reflects server truth
  await refreshChatMessages(chatId);
}
